import React, { useState } from 'react';
import { Badge } from '../../../../components/badge';
import type { SurveySummaryQuestion } from '../../types/survey.types';
import ViewAllResponsesModal from './ViewAllResponsesModal';

interface FileUploadResponseProps {
    question: SurveySummaryQuestion;
    totalResponses: number;
}

interface FileItem {
    name: string;
    size: string;
    uploadDate: string;
    fileType: string;
    url?: string;
}

interface FileTypeSummary {
    type: string;
    count: number;
}

const FileUploadResponse: React.FC<FileUploadResponseProps> = ({ 
    question, 
    totalResponses 
}) => {
    const { position, title, answered_count, options, total_pdf, total_image, total_document, total_other } = question;
    const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false);
    
    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';
    
    // Helper function to detect file type from URL
    const getFileTypeFromUrl = (url: string): string => {
        const extension = url.toLowerCase().split('.').pop()?.split('?')[0] || '';
        
        if (['pdf'].includes(extension)) return 'PDF';
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) return 'IMAGE';
        if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'].includes(extension)) return 'DOCUMENT';
        
        return 'OTHER';
    };
    
    // Parse file data from options (assuming each option represents a file)
    const files: FileItem[] = options.map(option => {
        // Parse the label to extract file information
        // Expected format: "filename.ext|size|uploadDate|fileType|url" or just "url" for simple file URL
        const parts = option.label.split('|');
        
        // If only one part, assume it's a URL
        if (parts.length === 1) {
            const url = parts[0];
            const fileName = url.split('/').pop() || url;
            const detectedFileType = getFileTypeFromUrl(url);
            return {
                name: fileName,
                size: '',
                uploadDate: new Date().toISOString().split('T')[0],
                fileType: detectedFileType,
                url: url
            };
        }
        
        // Otherwise, parse all parts
        return {
            name: parts[0] || option.label,
            size: parts[1] || '',
            uploadDate: parts[2] || new Date().toISOString().split('T')[0],
            fileType: parts[3] || 'OTHER',
            url: parts[4] || parts[0] // URL is in parts[4], fallback to parts[0] if not provided
        };
    });

    // Use real file type summary data from API
    const fileTypeSummary: FileTypeSummary[] = [
        { type: 'PDF', count: total_pdf || 0 },
        { type: 'IMAGE', count: total_image || 0 },
        { type: 'DOCUMENT', count: total_document || 0 },
        { type: 'OTHER', count: total_other || 0 }
    ];

    // Get file type display name
    const getFileTypeDisplayName = (fileType: string) => {
        const typeMap: Record<string, string> = {
            'PDF': 'PDF',
            'IMAGE': 'IMAGE',
            'DOCUMENT': 'DOCUMENT',
            'ZIP': 'OTHER',
            'OTHER': 'OTHER'
        };
        return typeMap[fileType] || 'OTHER';
    };

    // Get file type badge color
    const getFileTypeBadgeColor = (fileType: string) => {
        const colorMap: Record<string, string> = {
            'PDF': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
            'IMAGE': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
            'DOCUMENT': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
            'OTHER': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
        };
        return colorMap[fileType] || colorMap['OTHER'];
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 px-8 py-6 shadow-sm">
            {/* Header Section */}
            <div className="flex items-start gap-3 mb-6">
                {/* Question Icon */}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 bg-[#F2FCFA] dark:bg-emerald-900/30">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.9331 5.61914L6.92146 12.7741C6.60884 13.0868 6.43321 13.5108 6.43321 13.9529C6.43321 14.395 6.60884 14.819 6.92146 15.1316C7.23409 15.4443 7.65809 15.6199 8.10021 15.6199C8.54233 15.6199 8.96634 15.4443 9.27896 15.1316L16.2906 7.97664C16.9158 7.3515 17.267 6.50363 17.267 5.61955C17.267 4.73547 16.9158 3.88761 16.2906 3.26247C15.6655 2.63733 14.8176 2.28613 13.9335 2.28613C13.0495 2.28613 12.2016 2.63733 11.5765 3.26247L4.59396 10.3883C4.12346 10.8512 3.74927 11.4027 3.49296 12.0109C3.23666 12.6192 3.10331 13.2721 3.10063 13.9322C3.09794 14.5922 3.22596 15.2462 3.4773 15.8565C3.72865 16.4668 4.09834 17.0213 4.56505 17.488C5.03177 17.9548 5.58627 18.3245 6.19658 18.5758C6.80688 18.8271 7.46091 18.9552 8.12094 18.9525C8.78097 18.9498 9.43394 18.8164 10.0422 18.5601C10.6504 18.3038 11.2019 17.9296 11.6648 17.4591L18.6473 10.3333" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                
                <div className="flex-1">
                    {/* Question Title */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {position}. {title}
                    </h3>
                    
                    {/* Question Type Badge and Response Info */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <Badge 
                            variant="outline" 
                            className="text-[10px] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800"
                        >
                            File Upload
                        </Badge>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{answered_count}</span> of <span className="font-medium">{totalResponses}</span> responses
                        </div>
                        
                        <div className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                            {responseRate}% response rate
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Uploaded Files List */}
            {files.length > 0 ? (
                <div className="space-y-3 mb-6">
                    {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                {/* File Icon */}
                                <div className="flex-shrink-0">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8.5 1.5L11.5 4.5H9.5V1.5H8.5Z" fill="#6B7280"/>
                                        <path d="M7.5 1.5V4.5H5.5L8.5 1.5Z" fill="#6B7280"/>
                                        <path d="M3.5 2.5C3.5 2.22386 3.72386 2 4 2H7.5V4.5C7.5 4.77614 7.72386 5 8 5H10.5V13.5C10.5 13.7761 10.2761 14 10 14H4C3.72386 14 3.5 13.7761 3.5 13.5V2.5Z" fill="#6B7280"/>
                                    </svg>
                                </div>
                                
                                {/* File Info */}
                                <div className="flex-1 min-w-0">
                                    {file.url ? (
                                        <a 
                                            href={file.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline truncate flex items-center gap-1 group"
                                        >
                                            <span className="truncate">{file.name}</span>
                                            <svg 
                                                className="w-3 h-3 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" 
                                                fill="none" 
                                                stroke="currentColor" 
                                                viewBox="0 0 24 24"
                                            >
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                                                />
                                            </svg>
                                        </a>
                                    ) : (
                                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                            {file.name}
                                        </div>
                                    )}
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {file.size && file.size !== '0 KB' ? `${file.size} • ` : ''}{file.uploadDate}
                                    </div>
                                </div>
                                
                                {/* File Type Badge */}
                                <div className="flex-shrink-0">
                                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getFileTypeBadgeColor(file.fileType)}`}>
                                        {getFileTypeDisplayName(file.fileType)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 mb-6">
                    No files uploaded for this question
                </div>
            )}
            
            {/* File Type Summary */}
            {/* Pagination Info */}
            {files.length > 0 && (
                <div className="text-center mb-4 text-xs text-[#717182] dark:text-gray-400">
                    Showing {files.length} of {files.length} responses - 
                    <button 
                        className="underline hover:no-underline cursor-pointer ml-1"
                        onClick={() => setIsViewAllModalOpen(true)}
                    >
                        View All
                    </button>
                </div>
            )}
            <div className="grid grid-cols-4 gap-3 mx-8">
                {fileTypeSummary.map((summary, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                            {summary.count}
                        </div>
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-300 uppercase">
                            {getFileTypeDisplayName(summary.type)}
                        </div>
                    </div>
                ))}
            </div>
            
            {/* View All Modal */}
            <ViewAllResponsesModal
                isOpen={isViewAllModalOpen}
                onClose={() => setIsViewAllModalOpen(false)}
                question={question}
                totalResponses={totalResponses}
            />
        </div>
    );
};

export default FileUploadResponse;
